import { Transition, Popover as CPopover } from '@headlessui/react'
import { isFunction, startsWith } from 'lodash'
import { Fragment, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

const Popover = ({ children, ref, content, width, custom, triggerOnHover, placement = 'right', buttonClassName }) => {
  const hoverRef = useRef(null)
  const timeoutDuration = 200
  let timeout
  const [isMobile, setIsMobile] = useState(false)

  const { i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  const closePopover = () => {
    return hoverRef?.current?.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        bubbles: true,
        cancelable: true,
      })
    )
  }

  const onMouseEnter = open => {
    clearTimeout(timeout)
    if (open) return
    return hoverRef?.current?.click()
  }

  const onMouseLeave = open => {
    if (!open) return
    timeout = setTimeout(() => closePopover(), timeoutDuration)
  }

  const normalized = String(placement || 'right')
  const alignStart = startsWith(normalized, 'left') || normalized === 'bottom-start' || normalized === 'bottom'
  const alignEnd = startsWith(normalized, 'right')

  return (
    <CPopover ref={ref} property="" className="relative outline-none" onMouseLeave={triggerOnHover && onMouseLeave.bind(null, open)}>
      {({ open, close }) => (
        <>
          <CPopover.Button
            className={buttonClassName ? `outline-none ${buttonClassName}` : 'outline-none'}
            ref={hoverRef}
            onMouseEnter={triggerOnHover && onMouseEnter.bind(null, open)}
            onMouseLeave={triggerOnHover && onMouseLeave.bind(null, open)}
          >
            {children}
          </CPopover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <CPopover.Panel
              style={
                isMobile
                  ? custom
                    ? { width: '100%' }
                    : { width }
                  : custom
                  ? {
                      width: '590px',
                      maxWidth: 'calc(100vw - 1.5rem)',
                    }
                  : { width }
              }
              className={
                isMobile
                  ? 'fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50'
                  : classNames(
                      'absolute border rounded border-gray-200 z-[10000] shadow-md p-4 bg-white mt-3 min-w-[200px] max-h-[calc(100vh-5rem)] overflow-auto',
                      alignEnd || (isRTL && !alignStart) ? 'right-0 left-auto' : 'left-0 right-auto'
                    )
              }
            >
              {isMobile ? (
                <div className="bg-white shadow-lg rounded-lg w-full max-h-[100dvh] overflow-y-auto p-4 sm:p-6">{isFunction(content) ? content(close) : content}</div>
              ) : isFunction(content) ? (
                content(close)
              ) : (
                content
              )}
            </CPopover.Panel>
          </Transition>
        </>
      )}
    </CPopover>
  )
}

export default Popover
